import { Refresh, Sms } from "@mui/icons-material"
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { SharedAccessPatternSaEnum } from "shared/sharedAccessPatternsData"
import { SmsExpeditor } from "shared/smsExpeditor"
import {
  SmsExpeditorSuppliers,
  SmsExpeditorThingTypeMrn,
} from "shared/smsExpeditorData"
import { selectSaList } from "src/features/things/thingsSlice"
import { useAppSelector } from "../app/hooks"
import { useGetThingsQuery } from "../features/things/thingsApi"
import SaLayout from "./SaLayout"
import { Sl2Btn } from "./Sl2Btn"
import SmsExpeditorCard from "./SmsExpeditorCard"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

export const SaSmsExpeditorsPage: FC = () => {
  const { t } = useTranslation()

  const smsExpeditors = useAppSelector((state) =>
    selectSaList<SmsExpeditor>(state),
  ).filter((t) => t.thingTypeMrn === SmsExpeditorThingTypeMrn)

  const [formMode, setFormMode] = useState<"create" | "edit">("edit")
  const { isLoading: isLoadingSmsExpeditors, refetch } = useGetThingsQuery({
    params: {
      "ap.name": SharedAccessPatternSaEnum.SaGetSmsExpeditors,
    },
  })

  const { isLoading: isLoadingTenant, refetch: refetchTenant } =
    useGetThingsQuery({
      params: {
        "ap.name": SharedAccessPatternSaEnum.SaGetTenants,
      },
    })

  const refresh = useCallback(() => {
    refetch()
    refetchTenant()
  }, [refetch, refetchTenant])

  const isLoading = isLoadingSmsExpeditors || isLoadingTenant

  const [selectedSmsExpeditorId, setSelectedSmsExpeditorId] = useState<string>()

  const selectedSmsExpeditor = useMemo(
    () =>
      smsExpeditors?.find(
        (smsExpeditor) => smsExpeditor.id === selectedSmsExpeditorId,
      ),
    [smsExpeditors, selectedSmsExpeditorId],
  )
  return (
    <SaLayout selectedMenuItemLabelKey="smsExpeditors">
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 5 },
          width: "100%",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flexWrap="wrap"
          gap={2}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "primary.main",
              }}
            >
              {t("adminApp")}
            </Typography>
            <Stack direction="row" spacing={1.5} alignItems="baseline">
              <Typography
                variant="h4"
                sx={{
                  mt: 0.5,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "text.primary",
                  lineHeight: 1.15,
                }}
              >
                {t("smsExpeditors")}
              </Typography>
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: "1.1rem",
                  fontWeight: 500,
                }}
              >
                {smsExpeditors?.length ?? 0}
              </Typography>
            </Stack>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isLoading ? (
              <CircularProgress size={22} />
            ) : (
              <IconButton
                onClick={refresh}
                size="small"
                sx={{
                  bgcolor: "rgba(35, 66, 133, 0.06)",
                  color: "primary.main",
                  "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                }}
              >
                <Refresh fontSize="small" />
              </IconButton>
            )}
            <Sl2Btn
              variant="contained"
              color="primary"
              disableElevation
              onClick={() => {
                setFormMode("create")
              }}
            >
              {t("add")}
            </Sl2Btn>
          </Stack>
        </Stack>

        {/* Description card */}
        <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Stack direction="column" spacing={1}>
            <Typography variant="body2">
              {t("smsExpeditorsDescription1")}
            </Typography>
            <Typography variant="body2">
              {t("smsExpeditorsDescription2")}
            </Typography>
          </Stack>
        </Box>

        {/* Main content: list + detail */}
        <Stack direction="row" spacing={2}>
          <Stack direction="column" spacing={2}>
            <Paper
              variant="outlined"
              sx={{ p: 1, width: 400, borderTop: "3px solid #3A5163" }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={{ py: 2 }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body1" fontWeight={"bold"}>
                    {t("smsExpeditors")}
                  </Typography>
                  {!isLoading && (
                    <IconButton
                      onClick={refetch}
                      size="small"
                      sx={{
                        bgcolor: "rgba(35, 66, 133, 0.06)",
                        color: "primary.main",
                        "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  )}
                  {isLoading && <CircularProgress size={22} />}
                </Stack>
                <Sl2Btn
                  variant="contained"
                  color="primary"
                  disableElevation
                  onClick={() => {
                    setFormMode("create")
                  }}
                >
                  {t("add")}
                </Sl2Btn>
              </Stack>
              <Divider />
              <Stack direction="column" spacing={0}>
                {smsExpeditors &&
                  smsExpeditors.map((smsExpeditor, i) => {
                    const isSelected =
                      smsExpeditor.id === selectedSmsExpeditorId
                    const isLast = i === smsExpeditors.length - 1
                    const supplierLabel =
                      smsExpeditor.supplier === SmsExpeditorSuppliers.TWILIO
                        ? "Twilio"
                        : smsExpeditor.supplier === SmsExpeditorSuppliers.SOMUM
                          ? "Somum"
                          : smsExpeditor.supplier || "Twilio"
                    return (
                      <MenuItem
                        key={smsExpeditor.id}
                        selected={isSelected}
                        onClick={() => {
                          setSelectedSmsExpeditorId(smsExpeditor.id)
                          setFormMode("edit")
                        }}
                        sx={{
                          display: "block",
                          px: 2,
                          py: 1.25,
                          position: "relative",
                          borderBottom: isLast ? "none" : "1px solid",
                          borderColor: "rgba(0, 0, 0, 0.06)",
                          transition: "background-color 140ms ease",
                          "&:hover": {
                            bgcolor: "rgba(35, 66, 133, 0.04)",
                          },
                          "&.Mui-selected": {
                            bgcolor: "rgba(35, 66, 133, 0.08)",
                            "&:hover": { bgcolor: "rgba(35, 66, 133, 0.12)" },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              left: 0,
                              top: 8,
                              bottom: 8,
                              width: 3,
                              borderRadius: 2,
                              bgcolor: "primary.main",
                            },
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: 600,
                            color: isSelected ? "primary.main" : "text.primary",
                            lineHeight: 1.3,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {smsExpeditor.name}
                        </Typography>
                        <Typography
                          sx={{
                            mt: 0.25,
                            fontSize: "0.78rem",
                            fontFamily: "monospace",
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {smsExpeditor.fromNumber}
                        </Typography>
                        <Chip
                          label={supplierLabel}
                          size="small"
                          variant="outlined"
                          sx={{
                            mt: 0.75,
                            height: 20,
                            fontSize: "0.68rem",
                            fontWeight: 600,
                            letterSpacing: "0.04em",
                            borderColor: "rgba(0, 0, 0, 0.12)",
                            color: "text.secondary",
                          }}
                        />
                      </MenuItem>
                    )
                  })}
              </Stack>
            </Paper>
          </Stack>
          {!!selectedSmsExpeditor?.id || formMode === "create" ? (
            <Stack
              direction="column"
              spacing={2}
              sx={{
                width: "95%",
                maxWidth: 1000,
              }}
            >
              <SmsExpeditorCard
                smsExpeditor={
                  formMode === "create" ? undefined : selectedSmsExpeditor
                }
                mode={formMode}
                onComplete={refetch}
              />
            </Stack>
          ) : (
            <Stack
              // center the content
              direction="column"
              sx={{
                width: "95%",
                pt: 6,
              }}
              alignItems={"center"}
            >
              <Sms style={{ fontSize: "4em", color: "slategray" }} />
              <Typography>{t("pleaseSelectASmsExpeditor")}</Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default SaSmsExpeditorsPage
